import { debounce } from 'lodash';
import { bool, func, objectOf } from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { toastError, toastSuccess, toastWarning } from '@components/Toast';

import { sourceType } from '@features/trend-research/utils/utils';
import MoodboardCreate from '@features/trend-research/components/moodboard/addMoodboard/MoodboardCreate';

import { closeLoader, openLoader } from '@store/actions/LoaderAction';
import { setAllMoodboards, setSingleMoodboard } from '@store/actions/trend-research/TrendResearchAction';

import {
	findMyTrendMoodBoards,
	findTrendMoodBoards,
	saveProductToMoodboard,
	setMyMoodBoard
} from '@services/trendResearch';

const AddMoodBoard = ({
	isAddToMoodBoard,
	setIsAddToMoodBoard,
	isOpenMoodBoardModal,
	setIsOpenMoodBoardModal,
	selectedProduct
}) => {
	const dispatch = useDispatch();

	const [debouncedSearch, setDebouncedSearch] = useState(null);
	const [isGetMoodboard, setIsGetMoodboard] = useState(false);
	const [sort] = useState('id,desc');
	const [page, setPage] = useState(0);
	const [, setHasMore] = useState(true);
	const [threeApi, setThreeApi] = useState(false);
	const callCountRef = useRef(0);

	const selectedMoodboard = useSelector((store) => store.trendResearchStore.selectedMoodboard);
	const moodboards = useSelector((store) => store.trendResearchStore.allMoodboards);

	const location = useLocation();
	const { source } = location?.state || {};
	let userInfo = JSON.parse(localStorage.getItem('userInfo'));

	const fetchMoodboardList = async (searchQuery = '', newPage = 0) => {
		try {
			let params = `?userId=${userInfo.id}&size=20&page=${page}&sort=${sort}`;
			if (searchQuery) {
				params += `&search=${searchQuery}`;
			}

			const response = await findTrendMoodBoards(params); // Fetch API data
			if (response.data.length < 20) {
				setHasMore(false);
			}
			if (newPage === 0) {
				dispatch(setAllMoodboards(response.data));
			} else {
				const mergedData = [...moodboards, ...response.data];
				const uniqueData = [...new Map(mergedData.map((item) => [item.id, item])).values()];
				dispatch(setAllMoodboards(uniqueData));
			}
		} catch {
			/* empty */
		}
	};
	const handleSearch = debounce((searchValue) => {
		setDebouncedSearch(searchValue);
		fetchMoodboardList(searchValue, 0); // Reset to page 1 on new search
	}, 300);

	useEffect(() => {
		// Call the API on page or search change
		if (debouncedSearch || debouncedSearch === '' || page) {
			fetchMoodboardList(debouncedSearch, page);
		}
	}, [page, debouncedSearch]);

	const fetchSingleMoodboard = async () => {
		try {
			dispatch(openLoader());

			const response = await findMyTrendMoodBoards('');
			if (response) {
				await dispatch(setAllMoodboards([response]));
				await dispatch(setSingleMoodboard([response]));
			}

			dispatch(closeLoader());
		} catch {
			dispatch(closeLoader());
		}
	};

	useEffect(() => {
		fetchSingleMoodboard();
	}, []);

	useEffect(() => {
		const handleAddProductToMoodboard = async () => {
			if (selectedProduct && selectedMoodboard && !threeApi) {
				try {
					await addProductToMoodboard(selectedMoodboard); // Await the async operation
				} catch {
					/* empty */
				}
			}
		};
		if (isAddToMoodBoard) {
			handleAddProductToMoodboard();
		}
	}, [selectedProduct, selectedMoodboard, isAddToMoodBoard, threeApi]);

	const addProductToMoodboard = async (allmoodboards) => {
		try {
			dispatch(openLoader());
			if (allmoodboards && allmoodboards.length && selectedProduct) {
				let postData = {
					name: allmoodboards[0]?.name,
					imageUrl: selectedProduct?.imageUrl
				};
				if (sourceType[source] === 'BRAND') {
					postData.brandProductId = selectedProduct?.id;
				}
				if (sourceType[source] === 'INSTAGRAM') {
					postData.instagramProductId = selectedProduct?.id;
				}
				setIsAddToMoodBoard(false);
				try {
					const response = await saveProductToMoodboard(postData, allmoodboards[0]?.id);

					if (response.success) {
						toastSuccess('Added');
						await fetchSingleMoodboard();
					} else {
						toastError('Something went wrong!');
					}
				} catch (error) {
					if (error.response && error.response.status === 400) {
						toastWarning('Already added!');
					} else {
						// Handle any other unexpected errors
						toastWarning('Already added!');
					}
				}
			}

			dispatch(closeLoader());
		} catch {
			dispatch(closeLoader());
		}
	};

	const selectMyMoodboard = async (moodboard) => {
		try {
			dispatch(openLoader());
			let postData = { key: 'MOODBOARD_PREFERENCE', value: moodboard.id };

			setIsGetMoodboard(false);
			const response = await setMyMoodBoard(postData);
			if (response.success) {
				setDebouncedSearch(null);
				await fetchSingleMoodboard();
				await addProductToMoodboard([moodboard]);
				setThreeApi(false);
			}

			dispatch(closeLoader());
		} catch {
			dispatch(closeLoader());
		}
	};

	useEffect(() => {
		if (threeApi && moodboards.length > 0 && callCountRef.current === 0) {
			(async () => {
				try {
					callCountRef.current += 1;
					await selectMyMoodboard(moodboards[0]);
					//await addProductToMoodboard(moodboards);

					setThreeApi(false);
				} catch {
					/* empty */
				}
			})();
		}
	}, [threeApi, moodboards]);

	return (
		<>
			{isOpenMoodBoardModal && (
				<MoodboardCreate
					closeModal={setIsOpenMoodBoardModal}
					setPage={setPage}
					setDebouncedSearch={setDebouncedSearch}
					setIsGetMoodboard={setIsGetMoodboard}
					selectMyMoodboard={selectMyMoodboard}
					isGetMoodboard={isGetMoodboard}
					onSearch={handleSearch}
					debouncedSearch={debouncedSearch}
					setThreeApi={setThreeApi}
				/>
			)}
		</>
	);
};

AddMoodBoard.propTypes = {
	isAddToMoodBoard: bool.isRequired,
	setIsAddToMoodBoard: func,
	isOpenMoodBoardModal: bool.isRequired,
	setIsOpenMoodBoardModal: func,
	selectedProduct: objectOf
};

export default AddMoodBoard;
